import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, History as HistoryIcon, Filter } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { api } from "@/lib/api";

const SIGNAL_OPTS = ["ALL", "LONG", "SHORT", "WAIT"];
const ORIGIN_OPTS = ["ALL", "LIVE", "BACKTESTED"];

function fmtPct(v) { return v == null ? "—" : `${Number(v).toFixed(2)}%`; }

function ResultChip({ r }) {
  const cls = {
    WIN: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    LOSS: "text-red-300 border-red-500/40 bg-red-500/10",
    OPEN: "text-slate-300 border-slate-500/40 bg-slate-500/10",
    PENDING: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    NA: "text-slate-500 border-slate-700 bg-transparent",
  }[r] || "text-slate-500 border-slate-700 bg-transparent";
  return <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>{r || "—"}</span>;
}

function SignalChip({ s }) {
  const cls = s === "LONG" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" :
              s === "SHORT" ? "text-red-300 border-red-500/40 bg-red-500/10" :
              "text-slate-300 border-slate-500/40 bg-slate-500/10";
  return <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>{s}</span>;
}

function OriginChip({ o }) {
  const cls = o === "LIVE" ? "text-cyan-300 border-cyan-500/40 bg-cyan-500/10" : "text-slate-400 border-slate-600 bg-slate-500/5";
  return <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>{o}</span>;
}

export default function HistoryPage() {
  const [openSearch, setOpenSearch] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tickerFilter, setTickerFilter] = useState("");
  const [signalFilter, setSignalFilter] = useState("ALL");
  const [originFilter, setOriginFilter] = useState("ALL");
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (tickerFilter) params.ticker = tickerFilter.toUpperCase();
    if (signalFilter !== "ALL") params.signal = signalFilter;
    if (originFilter !== "ALL") params.origin = originFilter;
    api.get("/signal/history", { params })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tickerFilter, signalFilter, originFilter]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpenSearch(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpenSearch(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <HistoryIcon className="h-4 w-4 text-cyan-400" />
            <h1 className="mono text-lg font-bold text-slate-100 tracking-tight uppercase">Signal History</h1>
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500 ml-auto" data-testid="history-count">
              {rows.length} rows
            </span>
          </div>

          <div className="border border-[#1E293B] bg-[#0F1626] px-3 py-2 mb-3 flex flex-wrap items-center gap-3">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <input
              data-testid="history-ticker-filter"
              value={tickerFilter}
              onChange={(e) => setTickerFilter(e.target.value)}
              placeholder="Ticker filter…"
              className="mono text-xs bg-[#0B111C] border border-[#1E293B] px-2 py-1 text-slate-100 focus:outline-none focus:border-cyan-500/60 w-32"
            />
            <div className="flex items-center gap-1">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-500 mr-1">Signal</span>
              {SIGNAL_OPTS.map((s) => (
                <button
                  key={s}
                  data-testid={`history-signal-${s}`}
                  onClick={() => setSignalFilter(s)}
                  className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${signalFilter === s ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200" : "border-[#1E293B] text-slate-400 hover:text-slate-200"}`}
                >{s}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="mono text-[10px] uppercase tracking-widest text-slate-500 mr-1">Origin</span>
              {ORIGIN_OPTS.map((s) => (
                <button
                  key={s}
                  data-testid={`history-origin-${s}`}
                  onClick={() => setOriginFilter(s)}
                  className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${originFilter === s ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200" : "border-[#1E293B] text-slate-400 hover:text-slate-200"}`}
                >{s}</button>
              ))}
            </div>
          </div>

          <div className="border border-[#1E293B] bg-[#0F1626] overflow-auto max-h-[calc(100vh-260px)]">
            {loading && (
              <div className="p-6 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div className="p-6 text-slate-500 mono text-xs text-center">No snapshots matched.</div>
            )}
            {!loading && rows.length > 0 && (
              <table className="q-table w-full">
                <thead className="sticky top-0 bg-[#0B111C]">
                  <tr>
                    <th>Date</th><th>Ticker</th><th>Signal</th><th>Origin</th>
                    <th className="right">Long</th><th className="right">Short</th>
                    <th>Band</th><th className="right">Entry</th><th className="right">Stop</th><th className="right">T1</th>
                    <th className="right">5D</th><th className="right">20D</th>
                    <th>Result</th><th>Regime</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.snapshot_id}
                      data-testid={`history-row-${r.snapshot_id}`}
                      className="hover:bg-[#151C2C] cursor-pointer"
                      onClick={() => nav(`/stock/${r.ticker}`)}
                    >
                      <td className="text-slate-400">{r.as_of_ts ? new Date(r.as_of_ts).toISOString().slice(0, 10) : "—"}</td>
                      <td className="text-slate-100 font-bold">{r.ticker}</td>
                      <td><SignalChip s={r.signal} /></td>
                      <td><OriginChip o={r.origin} /></td>
                      <td className="right text-emerald-300">{r.long_score?.toFixed(1)}</td>
                      <td className="right text-red-300">{r.short_score?.toFixed(1)}</td>
                      <td className="text-slate-400">{r.strength_band}</td>
                      <td className="right text-slate-200">{r.ideal_entry != null ? `$${r.ideal_entry.toFixed(2)}` : "—"}</td>
                      <td className="right text-red-300">{r.stop_loss != null ? `$${r.stop_loss.toFixed(2)}` : "—"}</td>
                      <td className="right text-emerald-300">{r.t1 != null ? `$${r.t1.toFixed(2)}` : "—"}</td>
                      <td className={"right " + (r.return_5d > 0 ? "text-emerald-300" : r.return_5d < 0 ? "text-red-300" : "text-slate-500")}>{fmtPct(r.return_5d)}</td>
                      <td className={"right " + (r.return_20d > 0 ? "text-emerald-300" : r.return_20d < 0 ? "text-red-300" : "text-slate-500")}>{fmtPct(r.return_20d)}</td>
                      <td><ResultChip r={r.result} /></td>
                      <td className="text-slate-500 text-[10px]">{r.regime || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
      <TickerSearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <DisclaimerFooter />
    </div>
  );
}
